import { Link } from "react-router-dom";
import { FaArrowRightLong } from "react-icons/fa6";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

export default function Hero() {
  const { t } = useTranslation();
  return (
    <section className="relative w-full min-h-screen overflow-hidden">

      {/* ================= BACKGROUND IMAGE ================= */}
      {/* Mobile Image */}
      <motion.img
        src="/Hero-mobile.webp"
        alt="Sustainable Farming Background Mobile"
        className="absolute inset-0 w-full h-full object-cover md:hidden"
        initial={{ scale: 1.1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.8, ease: "easeOut" }}
      />

      {/* Desktop Image */}
      <motion.img
        src="/hero-section.webp"
        alt="Sustainable Farming Background"
        className="absolute inset-0 w-full h-full object-cover hidden md:block"
        initial={{ scale: 1.1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.8, ease: "easeOut" }}
      />

      {/* ================= OVERLAY ================= */}
    
      <motion.div
        className="absolute inset-0 bg-black/30 md:hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      />

      <motion.div
        className="absolute inset-0 hidden md:block bg-gradient-to-r from-black/70 via-black/50 to-black/30"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      />


      {/* ================= CONTENT =============== */}
      <div
        className="relative z-10 flex items-end justify-between min-h-screen 
                   px-6 sm:px-10 lg:px-18 pb-20"
      >
        {/* ================= LEFT SIDE ================= */}
        <motion.div
          className="max-w-4xl w-full"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: {
              transition: { staggerChildren: 0.15 }
            }
          }}
        >
          {/* Tagline */}
          <motion.p
            className="inline-block text-[#7EDE56] bg-black/40 ring-1 ring-[#7EDE56]/50 px-3 py-1 mb-4 rounded-full uppercase tracking-[0.2em] text-[10px] sm:text-xs font-semibold"
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 }
            }}
            transition={{ duration: 0.6 }}
          >
            {t("hero_tagline")}
          </motion.p>

          {/* Heading */}
          <motion.h1
            className="text-white font-semibold leading-snug
                       text-2xl sm:text-3xl md:text-4xl lg:text-4xl max-w-xl"
            variants={{
              hidden: { opacity: 0, y: 30 },
              visible: { opacity: 1, y: 0 }
            }}
            transition={{ duration: 0.7 }}
          >
            {t("hero_title")}
            <span className="block text-[#7EDE56]">
              {t("hero_subtitle")}
            </span>
          </motion.h1>

          {/* Description */}
          <motion.p
            className="mt-4 text-white/90 text-xs sm:text-sm max-w-md leading-relaxed"
            variants={{
              hidden: { opacity: 0, y: 25 },
              visible: { opacity: 1, y: 0 }
            }}
            transition={{ duration: 0.6 }}
          >
            {t("hero_description")}
          </motion.p>

          {/* Button */}
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 }
            }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/automation-detail" className="inline-block mt-6">
              <button className="group inline-flex items-center gap-2.5 bg-[#7EDE56] text-[#002F3F] hover:text-white font-medium rounded-lg hover:bg-[#002F3F] cursor-pointer px-6 py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 hover:gap-4">
                {t("hero_cta")}
                <FaArrowRightLong className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </Link>
          </motion.div>
        </motion.div>

        {/* ================= RIGHT SIDE BOX ================= */}
        <motion.div
          className="hidden md:flex flex-col max-w-sm bg-[#ffffff]/15 p-5  rounded-lg text-white text-sm sm:text-[14px] leading-relaxed"
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, delay: 0.4 }}
        >
          <p>
            {t("hero_right_heading")} {t("hero_right_text")}
          </p>

          <Link to="/projects" className="mt-4 self-start">
            <button className="group inline-flex items-center gap-2.5 bg-gray-100/20 shadow-2xl text-white px-5 py-2 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 hover:bg-[#002F3F] hover:gap-3">
              {t("hero_right_cta")}
              <FaArrowRightLong className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}